/**
 * Scheduling Link Card Component (Feature 035)
 *
 * Allows Service Providers to add/edit their Calendly scheduling link
 */

import { useState, useEffect } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Calendar, Edit2, ExternalLink, Check, X } from 'lucide-react';

type SchedulingLinkCardProps = {
  calendlyUrl?: string;
  onSave: (url: string) => Promise<void>;
  isLoading?: boolean;
};

export function SchedulingLinkCard({
  calendlyUrl,
  onSave,
  isLoading = false,
}: SchedulingLinkCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [url, setUrl] = useState(calendlyUrl || 'https://');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setUrl(calendlyUrl || 'https://');
  }, [calendlyUrl]);

  const validateUrl = (value: string): boolean => {
    const trimmedValue = value.trim();

    // Empty or just "https://" is valid (removes the URL)
    if (!trimmedValue || trimmedValue === 'https://') {
      setError(null);
      return true;
    }

    try {
      const parsed = new URL(trimmedValue);

      if (parsed.protocol !== 'https:' || parsed.hostname !== 'calendly.com') {
        setError('Calendly URL must start with https://calendly.com/');
        return false;
      }

      // Require at least one non-empty path segment after the domain
      const normalizedPath = parsed.pathname.replace(/^\/+|\/+$/g, '');
      if (!normalizedPath) {
        setError('Calendly URL must include a scheduling path after the domain.');
        return false;
      }
    } catch {
      setError('Please enter a valid URL.');
      return false;
    }

    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateUrl(url)) {
      return;
    }

    setIsSaving(true);
    try {
      // Save "https://" as empty string (removes the URL)
      const urlToSave = url.trim() === 'https://' ? '' : url.trim();
      await onSave(urlToSave);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setUrl(calendlyUrl || 'https://');
    setError(null);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-sage" />
          <h3 className="text-lg font-medium text-text-primary">Scheduling Link</h3>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </Button>
        )}
      </div>

      <p className="text-sm text-text-secondary mb-4">
        Add your Calendly link to allow care seekers to book consultations directly through your
        calendar.
      </p>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <Label htmlFor="calendlyUrl">Calendly URL</Label>
            <Input
              id="calendlyUrl"
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError(null);
              }}
              disabled={isSaving}
              aria-invalid={!!error}
              className="mt-2"
              placeholder="https://calendly.com/your-username"
              autoComplete="off"
            />
            {error && (
              <p className="text-sm text-red-600 mt-1" role="alert">
                {error}
              </p>
            )}
            <p className="text-sm text-text-secondary mt-1">
              Example: https://calendly.com/your-username/30min
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-bquest-green hover:bg-bquest-green/90 flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {calendlyUrl ? (
            <div className="flex items-center gap-2 p-3 bg-card-subtle rounded-md">
              <div className="flex-1 truncate">
                <p className="text-sm font-medium text-text-primary truncate">{calendlyUrl}</p>
              </div>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 text-sage hover:opacity-80"
                aria-label="Open Calendly link in new tab"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <div className="p-4 bg-card-subtle rounded-md border border-dashed border-border-subtle">
              <p className="text-sm text-text-secondary text-center">
                No scheduling link added yet
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
